import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { BadgeVariant } from '@/admin/ui/Badge';
import { Badge } from '@/admin/ui/Badge';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { PageTitle } from '@/admin/ui/Page';
import type { NormalizedQuestion } from '@/admin/ui/QuestionAnswerView';
import { QuestionAnswerView } from '@/admin/ui/QuestionAnswerView';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { FeedbackEditControls } from '@/staff/FeedbackEditControls';
import i18n from '~/i18n';

type GradingSubmissionDetail = components['schemas']['GradingSubmissionDetail'];
type GradingQuestion = components['schemas']['GradingQuestion'];

export const Route = createFileRoute('/admin/feedbacks/$submissionId')({
  // Editing or deleting stored feedback is administrator-tier only, so lower
  // tiers get the 404 screen.
  staticData: { minTier: 'ADMIN' },
  component: FeedbackDetail,
});

function statusBadge(status: string): {
  label: string;
  variant: BadgeVariant;
} {
  switch (status) {
    case 'GRADED':
      return {
        label: i18n.t('admin.feedbacks.status.graded'),
        variant: 'success',
      };
    case 'PENDING':
      return {
        label: i18n.t('admin.feedbacks.status.pending'),
        variant: 'warning',
      };
    case 'FAILED':
      return {
        label: i18n.t('admin.feedbacks.status.failed'),
        variant: 'error',
      };
    default:
      return { label: status, variant: 'neutral' };
  }
}

function toNormalized(q: GradingQuestion): NormalizedQuestion {
  const hasAnswer =
    q.user_answer !== null &&
    q.user_answer !== undefined &&
    !(Array.isArray(q.user_answer) && q.user_answer.length === 0) &&
    !(typeof q.user_answer === 'string' && q.user_answer === '');
  const isMultiple = q.type === 'choose_one' || q.type === 'choose_multiple';
  const choices = q.answers.map((a, i) => ({
    index: i,
    text: a.answer,
    is_correct: a.is_correct,
  }));
  // For multiple choice, derive boolean[] from is_chosen flags if user_answer
  // doesn't already supply it.
  let userAnswer: NormalizedQuestion['user_answer'] = q.user_answer ?? null;
  if (isMultiple && !Array.isArray(userAnswer)) {
    userAnswer = q.answers.map((a) => a.is_chosen);
  }
  return {
    question_id: q.id,
    type: q.type,
    text: q.content,
    choices,
    user_answer: userAnswer,
    has_answer: hasAnswer,
    is_correct: q.score > 0,
    feedback: q.feedback,
  };
}

function FeedbackDetail() {
  const { t } = useTranslation();
  const { submissionId } = Route.useParams();
  const { data, isLoading, error } = api.adminGetFeedbackSubmission.useQuery({
    path: { submission_id: Number(submissionId) },
  });

  if (isLoading) {
    return <LoadingMessage>{t('admin.feedbacks.loadingOne')}</LoadingMessage>;
  }
  if (error) {
    return <ErrorMessage>{t('admin.feedbacks.errorLoadingOne')}</ErrorMessage>;
  }
  if (!data) {
    return <ErrorMessage>{t('admin.feedbacks.notFound')}</ErrorMessage>;
  }

  const { label, variant } = statusBadge(data.status);
  const detail = data as GradingSubmissionDetail;
  // Keyed by question: the detail carries no feedback row id, and the pair
  // (submission, question) is unique.
  const answerFeedbackByQuestion = new Map(
    detail.answer_feedbacks.map((entry) => [entry.question_id, entry.content]),
  );

  return (
    <Container>
      <BackLink to="/admin/feedbacks/">
        {t('admin.feedbacks.backToList')}
      </BackLink>

      <Header>
        <PageTitle>
          {t('admin.feedbacks.title', {
            id: detail.id,
            interpolation: { escapeValue: false },
          })}
        </PageTitle>
        <Badge $variant={variant} $size="md">
          {label}
        </Badge>
      </Header>

      <Section>
        <SectionTitle>{t('admin.common.overview')}</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.quizSubmissions.student')}</FieldLabel>
            <FieldValue>{detail.student.display_name}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.students.studyHelper')}</FieldLabel>
            <FieldValue>
              {detail.helper ? detail.helper.display_name : '—'}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.quizSubmissions.course')}</FieldLabel>
            <FieldValue>{detail.course.title}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.topics.lesson')}</FieldLabel>
            <FieldValue>{detail.lesson.title}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.quizSubmissions.score')}</FieldLabel>
            <FieldValue>{detail.score}%</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.feedbacks.attempt')}</FieldLabel>
            <FieldValue>#{detail.attempt_number}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.quizSubmissions.submitted')}</FieldLabel>
            <FieldValue>
              {new Date(detail.created_at).toLocaleString()}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.feedbacks.graded')}</FieldLabel>
            <FieldValue>
              {detail.graded_at
                ? new Date(detail.graded_at).toLocaleString()
                : '—'}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      {detail.final_feedback ? (
        <Section>
          <SectionTitle>{t('admin.feedbacks.finalThought')}</SectionTitle>
          <FinalFeedbackBlock>{detail.final_feedback}</FinalFeedbackBlock>
          <FeedbackEditControls
            submissionId={detail.id}
            target={{ kind: 'final' }}
            content={detail.final_feedback}
          />
        </Section>
      ) : null}

      <Section>
        <SectionTitle>
          {t('admin.feedbacks.questionsAnswersFeedback')}
        </SectionTitle>
        {detail.questions.length === 0 ? (
          <EmptyMessage>{t('admin.quizSubmissions.noQuestions')}</EmptyMessage>
        ) : (
          detail.questions.map((q, idx) => (
            <div key={q.id}>
              <QuestionAnswerView question={toNormalized(q)} index={idx + 1} />
              {answerFeedbackByQuestion.has(q.id) && (
                <FeedbackEditControls
                  submissionId={detail.id}
                  target={{ kind: 'answer', questionId: q.id }}
                  content={answerFeedbackByQuestion.get(q.id) ?? ''}
                />
              )}
            </div>
          ))
        )}
      </Section>
    </Container>
  );
}

const Container = styled.div``;

const BackLink = styled(Link)`
  align-items: center;
  color: var(--blue);
  display: inline-flex;
  font-size: 0.875rem;
  gap: 0.5rem;
  margin-bottom: 1rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const FinalFeedbackBlock = styled.div`
  background: color-mix(in srgb, var(--blue) 8%, transparent);
  border-left: 3px solid var(--blue);
  border-radius: 4px;
  padding: 1rem;
  white-space: pre-wrap;
`;

const EmptyMessage = styled.div`
  color: var(--grey-600);
  padding: 1rem;
`;

const LoadingMessage = styled.div`
  color: var(--grey-600);
  padding: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  padding: 2rem;
  text-align: center;
`;
